import { InfiniteData, QueryClient } from '@tanstack/react-query'
import {
  DiscussionPage,
  discussionQueryKey,
  getCommentRow,
} from '../../api/comments'
import { isDeniedRequest } from '../../api/helpers'
import { peopleQueryKey } from '../../api/people'
import { CommentRow, CommentsSurface } from '../../generated/api/comment-row'
import { CommentUpdatedPayload } from '../../generated/api/live-events'
import { isPostCached, patchQueries, refreshPostRow } from './postRowCache'

type Discussion = InfiniteData<CommentsSurface, DiscussionPage>
type CommentUpdate = (comment: CommentRow) => CommentRow

export const applyToComments = (
  comments: CommentRow[],
  commentId: string,
  update: CommentUpdate
): CommentRow[] =>
  comments.some((comment) => comment.id === commentId)
    ? comments.map((comment) =>
        comment.id === commentId ? update(comment) : comment
      )
    : comments

export const dropComment = (
  comments: CommentRow[],
  commentId: string
): CommentRow[] =>
  comments.some((comment) => comment.id === commentId)
    ? comments.filter((comment) => comment.id !== commentId)
    : comments

export const likedComment = (
  comment: CommentRow,
  liked: boolean
): CommentRow =>
  comment.likes.viewerLiked === liked
    ? comment
    : {
        ...comment,
        likes: {
          viewerLiked: liked,
          count: Math.max(0, comment.likes.count + (liked ? 1 : -1)),
        },
        actions: {
          ...comment.actions,
          canOpenLikers: liked || comment.likes.count > 1,
        },
      }

export const mapDiscussion = (
  data: Discussion | undefined,
  mapItems: (items: CommentRow[]) => CommentRow[]
): Discussion | undefined => {
  if (data == null) {
    return data
  }
  const pages = data.pages.map((page) => {
    const items = mapItems(page.items)
    return items === page.items ? page : { ...page, items }
  })
  return pages.every((page, index) => page === data.pages[index])
    ? data
    : { ...data, pages }
}

export const updateComment = (
  client: QueryClient,
  postId: string,
  commentId: string,
  update: CommentUpdate
): void => {
  patchQueries<Discussion>(client, discussionQueryKey(postId), (data) =>
    mapDiscussion(data, (items) => applyToComments(items, commentId, update))
  )
}

export const removeComment = (
  client: QueryClient,
  postId: string,
  commentId: string
): void => {
  patchQueries<Discussion>(client, discussionQueryKey(postId), (data) =>
    mapDiscussion(data, (items) => dropComment(items, commentId))
  )
}

export const refreshComment = async (
  client: QueryClient,
  postId: string,
  commentId: string
): Promise<void> => {
  const { item } = (await getCommentRow(commentId)).data.data
  updateComment(client, postId, commentId, () => item)
}

const isCommentCached = (
  client: QueryClient,
  postId: string,
  commentId: string
): boolean =>
  client
    .getQueriesData<Discussion>({ queryKey: discussionQueryKey(postId) })
    .some(([, data]) =>
      data?.pages.some((page) =>
        page.items.some((comment) => comment.id === commentId)
      )
    )

const refreshCachedPostRow = async (
  client: QueryClient,
  postId: string
): Promise<void> => {
  if (isPostCached(client, postId, { inPinned: true })) {
    await refreshPostRow(client, postId)
  }
}

export const applyCommentUpdated = async (
  client: QueryClient,
  { postId, commentId, action }: CommentUpdatedPayload
): Promise<void> => {
  if (action === 'created') {
    await Promise.all([
      client.invalidateQueries({ queryKey: discussionQueryKey(postId) }),
      refreshCachedPostRow(client, postId),
    ])
  } else if (action === 'deleted') {
    removeComment(client, postId, commentId)
    await refreshCachedPostRow(client, postId)
  } else {
    await Promise.all([
      isCommentCached(client, postId, commentId) &&
        refreshComment(client, postId, commentId).catch((error) => {
          if (isDeniedRequest(error)) {
            removeComment(client, postId, commentId)
          }
        }),
      action === 'liked' &&
        client.invalidateQueries({
          queryKey: peopleQueryKey({ kind: 'comment-likers', id: commentId }),
        }),
    ])
  }
}
